'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface ScrollContextType {
  scrollY: number
  scrollHeight: number
  progress: number
  isAtTop: boolean
  isScrolling: boolean
}

const defaultScrollValues: ScrollContextType = {
  scrollY: 0,
  scrollHeight: 0,
  progress: 0,
  isAtTop: true,
  isScrolling: false,
}

const ScrollContext = createContext<ScrollContextType | null>(null)

export const useScroll = () => {
  const context = useContext(ScrollContext)
  if (!context) {
    // Provider가 없을 때 기본값 반환 (서버 렌더링 시)
    return defaultScrollValues
  }
  return context
}

interface ScrollProviderProps {
  children: ReactNode
  onScrollUpdate?: (updateFn: (y: number) => void) => void
}

export const ScrollProvider = ({ children, onScrollUpdate }: ScrollProviderProps) => {
  const [scrollY, setScrollY] = useState(0)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isAtTop, setIsAtTop] = useState(true)
  const [isScrolling, setIsScrolling] = useState(false)

  const updateScroll = (y: number) => {
    setScrollY(y)
    setIsAtTop(y <= 10)
    setIsScrolling(true)

    // 페이지의 전체 스크롤 가능한 높이 계산
    const documentHeight = document.documentElement.scrollHeight
    const windowHeight = window.innerHeight
    const maxScroll = documentHeight - windowHeight

    setScrollHeight(maxScroll)
    setProgress(maxScroll > 0 ? y / maxScroll : 0)

    setTimeout(() => setIsScrolling(false), 150)
  }

  useEffect(() => {
    onScrollUpdate?.(updateScroll)
  }, [onScrollUpdate])

  return (
    <ScrollContext.Provider value={{ scrollY, scrollHeight, progress, isAtTop, isScrolling }}>
      {children}
    </ScrollContext.Provider>
  )
}

export { ScrollContext }
